/**
 * NewEnrollmentDialog — iMikel-Style Neuanmeldungs-Maske.
 *
 * Legt in einer einzigen Transaktion an:
 *   - Schüler:in (Student) mit optionaler Duplikate-Warnung
 *   - Zahlungspflichtige:r (Guardian, existierender wird per E-Mail wiederverwendet)
 *   - Familie (existierende oder neue)
 *   - Bank/SEPA-Mandat (optional)
 *   - Belegung — entweder bestehender Kurs (Dropdown) ODER neuer Kurs (Fach/Lehrkraft/Zeit/Raum)
 *
 * Zusatzfeatures:
 *   - Live-Duplikate-Check bei Namen+Geburtsdatum (debounced 500 ms)
 *   - Adress-Autovervollständigung: Zahler-Adresse mit einem Klick zum Schüler kopieren
 *
 * Backend-Endpoints:
 *   POST /api/v1/enrollments/quick-create
 *   GET  /api/v1/enrollments/duplicate-check
 */
import React, { useEffect, useState, useRef } from 'react';
import { matchesFreeText } from '@/components/search/SearchBuilder';
import { X, Save, User as UserIcon, Users, Euro, GraduationCap, Loader2,
         ArrowDown, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { PlzOrtBridge } from '@/components/address/PlzOrtBridge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DateInput from '@/components/ui/date-input';
import { formatDate, formatClocks, formatTime, WEEKDAYS_SHORT, tariffFormName, formatDeNumber } from '@/lib/utils';
import { retryOnCollision } from '@/components/schedule/collisionWarning';
import { resolveEnrollmentSubjectPrefill } from '@/lib/registrationToEnrollmentPrefill';
import { AREAL_OPTIONS, defaultArealForTenant, selectableTeachers } from '../utils';
import { TURNUS_OPTIONS, parseCourseRhythm } from '../enrollmentSearchCriteria';
import { useTenant } from '@/contexts/TenantContext';
import { lookupIbanInfo } from '@/lib/iban';
import { formatApiError } from '@/lib/errors';

// DateInput liefert { target: { value } }. Wer das Ereignis selbst speichert,
// schickt der Schnittstelle ein Objekt statt eines Datums.
function datumAusFeld(wert) {
  if (wert && typeof wert === 'object' && wert.target) return wert.target.value || '';
  return typeof wert === 'string' ? wert : '';
}

function isoOderLeer(wert) {
  const roh = datumAusFeld(wert).slice(0, 10);
  return roh || null;
}

const DAYS = [
  { value: '0', label: 'Montag' }, { value: '1', label: 'Dienstag' },
  { value: '2', label: 'Mittwoch' }, { value: '3', label: 'Donnerstag' },
  { value: '4', label: 'Freitag' }, { value: '5', label: 'Samstag' },
  { value: '6', label: 'Sonntag' },
];

const emptyForm = (tenant) => ({
  student: {
    first_name: '', last_name: '', date_of_birth: '',
    email: '', phone: '', address: '', postal_code: '', city: '',
    gkz: 'GRE', areal: defaultArealForTenant(tenant), notes: '',
  },
  payer: {
    first_name: '', last_name: '',
    email: '', phone: '', address: '', postal_code: '', city: '',
  },
  bank: { account_holder: '', iban: '', bic: '' },
  enrollment: {
    course_id: '', start_date: new Date().toISOString().slice(0, 10), end_date: '', status: 'active',
    instrument: '',
    needs_rental: false,
    new_course: {
      subject_id: '', teacher_id: '', room_id: '', tariff_id: '',
      day_of_week: '', start_time: '', duration_minutes: '45', rhythm: 'weekly', name: '',
    },
  },
});

const Section = ({ icon: Icon, title, color, headerRight, children }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-4">
    <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-100">
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 text-${color}-600`} />
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">{title}</h3>
      </div>
      {headerRight}
    </div>
    <div className="grid grid-cols-6 gap-2.5">{children}</div>
  </div>
);

const Fld = ({ label, value, onChange, testid, span = 3, type = 'text', required = false, placeholder }) => (
  <div className={`col-span-${span}`}>
    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
      {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </Label>
    <Input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-8 text-sm"
      data-testid={testid}
    />
  </div>
);

const SelFld = ({ label, value, onValueChange, options, span = 3, testid, required = false, placeholder = '—' }) => (
  <div className={`col-span-${span}`}>
    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
      {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </Label>
    <Select value={value || ''} onValueChange={onValueChange}>
      <SelectTrigger className="h-8 text-sm" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} className="text-sm">{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export default function NewEnrollmentDialog({ open, onClose, onSaved, prefill = null, variant = 'belegung' }) {
  const { activeTenant } = useTenant();
  const [data, setData] = useState(() => emptyForm(activeTenant));
  const existingStudentId = prefill?.existingStudentId || null;
  const existingGuardianId = prefill?.existingGuardianId || null;
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [subjectsRaw, setSubjectsRaw] = useState([]);  // Original-Subjects mit ID+Name
  const [instrumentTypes, setInstrumentTypes] = useState([]);  // distinct instrument.type Werte für datalist
  const [saving, setSaving] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [courseMode, setCourseMode] = useState('existing'); // 'existing' | 'new'
  const [courseQuery, setCourseQuery] = useState('');
  const [courseListOpen, setCourseListOpen] = useState(false);
  const [dupCandidates, setDupCandidates] = useState([]);
  const [dupExactMatch, setDupExactMatch] = useState(false);
  const [dupDismissed, setDupDismissed] = useState(false);
  const dupTimerRef = useRef(null);

  const set = (section, field, value) =>
    setData((d) => ({ ...d, [section]: { ...d[section], [field]: value } }));
  const setNc = (field, value) =>
    setData((d) => ({ ...d, enrollment: { ...d.enrollment, new_course: { ...d.enrollment.new_course, [field]: value } } }));

  // Parse subjects: extrahiere Instrument-Präfix (alles vor Einzel/Gruppe/Karte/Abo/Ensemble/Xer).
  // Wenn > 30 % der Subjects "combined" sind → Combined-Mode: Fach + Unterrichtsform aus subjects.
  // Sonst Clean-Mode: Fach = subjects, Unterrichtsform = separate Tariff-Tabelle.
  const FORM_KEYWORDS_RE = /\s+(Einzel|Gruppe|Karte|Abo|Ensemble|Kombi|GEMA|Aufschlag|\d+(?:-\d+)?er)\b/i;
  const parseSubject = (name) => {
    const m = FORM_KEYWORDS_RE.exec(name || '');
    if (m) {
      return {
        instrument: name.slice(0, m.index).trim() || name.trim(),
        form: name.slice(m.index).trim(),
      };
    }
    return { instrument: name.trim(), form: '' };
  };
  const { combinedMode, subjects, subjectFormsByInstrument } = React.useMemo(() => {
    if (!subjectsRaw.length) return { combinedMode: false, subjects: [], subjectFormsByInstrument: {} };
    const parsed = subjectsRaw.map((s) => ({ id: s.id, name: s.name, ...parseSubject(s.name) }));
    const combinedCount = parsed.filter((p) => p.form).length;
    const combined = combinedCount / parsed.length > 0.3;
    if (!combined) {
      return {
        combinedMode: false,
        subjects: subjectsRaw.map((s) => ({ value: s.id, label: s.name })),
        subjectFormsByInstrument: {},
      };
    }
    // Combined-Mode: distinct instruments + Sub-Forms
    const byInstrument = {};
    for (const p of parsed) {
      if (!p.instrument) continue;
      if (/^\d+er/i.test(p.instrument)) continue; // "10er Karte" ohne Instrument überspringen
      if (!byInstrument[p.instrument]) byInstrument[p.instrument] = [];
      byInstrument[p.instrument].push({ value: p.id, label: p.form || '(Standard)', rawName: p.name });
    }
    const instrumentOptions = Object.keys(byInstrument).sort().map((instr) => ({
      value: `__combined__${instr}`, label: instr, forms: byInstrument[instr],
    }));
    return { combinedMode: true, subjects: instrumentOptions, subjectFormsByInstrument: byInstrument };
  }, [subjectsRaw]);

  // Auto-generierte Bezeichnung: Fach · Tarif · Zeit — ohne Lehrkraft
  //   "Gitarre Einzel 30 Min Di 16.45"
  const autoLabel = React.useMemo(() => {
    const nc = data.enrollment.new_course;
    let subj = '';
    let formName = '';
    if (combinedMode) {
      // In Combined-Mode: subject_id ist "__combined__<instrument>" → Instrument-Name
      if (nc.subject_id?.startsWith('__combined__')) subj = nc.subject_id.replace('__combined__', '');
      // Form kommt aus tariff_id (dort speichern wir das gewählte Sub-Subject)
      const subjRow = subjectsRaw.find((s) => s.id === nc.tariff_id);
      formName = subjRow ? parseSubject(subjRow.name).form : '';
    } else {
      subj = subjectsRaw.find((s) => s.id === nc.subject_id)?.name || '';
      formName = tariffs.find((t) => t.value === nc.tariff_id)?.raw?.name || '';
    }
    const dayIndex = parseInt(nc.day_of_week, 10);
    const day = nc.day_of_week !== '' ? (WEEKDAYS_SHORT[dayIndex] || '') : '';
    const time = formatTime(nc.start_time);
    const parts = [subj, formName].filter(Boolean);
    if (day && time) parts.push(`${day} ${time}`);
    return parts.join(' ');
  }, [data.enrollment.new_course, subjectsRaw, tariffs, combinedMode]);

  // Reset + Stammdaten laden
  useEffect(() => {
    if (!open) {
      setData(emptyForm(activeTenant));
      setCourseMode('existing');
      setCourseQuery('');
      setCourseListOpen(false);
      setDupCandidates([]); setDupExactMatch(false); setDupDismissed(false);
      return;
    }
    setCourseMode(variant === 'neuerfassung' ? 'new' : 'existing');
    setLoadingCourses(true);
    Promise.all([
      api.get('/courses', { params: { limit: 500 } }).catch(() => ({ data: [] })),
      api.get('/subjects').catch(() => ({ data: [] })),
      api.get('/teachers').catch(() => ({ data: [] })),
      api.get('/locations/rooms').catch(() => api.get('/rooms').catch(() => ({ data: [] }))),
      api.get('/tariffs-discounts/tariffs').catch(() => ({ data: [] })),
      api.get('/instrument-rental/instruments').catch(() => ({ data: [] })),
    ]).then(([cRes, sRes, tRes, rRes, tarRes, iRes]) => {
      const cItems = Array.isArray(cRes.data) ? cRes.data : (cRes.data?.items || []);
      setCourses(cItems.map((c) => ({
        value: c.id,
        label: `${c.name || c.subject_name || 'Kurs'}${c.teacher_name ? ` · ${c.teacher_name}` : ''}${c.day_of_week != null && c.start_time ? ` · ${WEEKDAYS_SHORT[c.day_of_week] || ''} ${formatTime(c.start_time)}` : ''}`,
      })));
      const sItems = Array.isArray(sRes.data) ? sRes.data : (sRes.data?.items || []);
      // Archivierte Subjects nach Alt-Split ausblenden (Präfix „[ARCHIV] ")
      setSubjectsRaw(sItems.filter((s) => !(s.name || '').startsWith('[ARCHIV] ')));
      const tItems = Array.isArray(tRes.data) ? tRes.data : (tRes.data?.items || []);
      setTeachers(selectableTeachers(tItems).map((t) => ({ value: t.id, label: `${t.first_name || ''} ${t.last_name || ''}`.trim() })));
      const rItems = Array.isArray(rRes.data) ? rRes.data : (rRes.data?.items || []);
      setRooms(rItems.map((r) => {
        const roomName = r.name || (r.number != null ? `Raum ${r.number}` : 'Raum');
        const loc = r.location_name || r.location?.name || '';
        return {
          value: r.id,
          label: loc ? `${loc} · ${roomName}` : roomName,
        };
      }));
      const tarItems = Array.isArray(tarRes.data) ? tarRes.data : (tarRes.data?.items || []);
      setTariffs(tarItems.map((tar) => ({
        value: tar.id,
        label: `${tariffFormName(tar.name)}${tar.amount ? ` · ${formatDeNumber(tar.amount)} €` : ''}`,
        raw: tar,
      })));
      // Distinct instrument-types aus Inventar
      const iItems = Array.isArray(iRes.data) ? iRes.data : (iRes.data?.items || []);
      const types = Array.from(new Set(iItems.map((i) => (i.type || '').trim()).filter(Boolean))).sort();
      setInstrumentTypes(types);
    }).finally(() => setLoadingCourses(false));
  }, [open, variant]);

  // Prefill aus UV (bestehende:r Teilnehmer:in / Zahler:in) oder Online-Anmeldung
  useEffect(() => {
    if (!open) {
      setData(emptyForm(activeTenant));
      return;
    }
    if (!prefill) return;
    setData((d) => ({
      ...d,
      student: {
        ...d.student,
        ...(prefill.student || {}),
      },
      payer: {
        ...d.payer,
        ...(prefill.payer || {}),
      },
      bank: {
        ...d.bank,
        ...(prefill.bank || {}),
      },
      enrollment: {
        ...d.enrollment,
        ...(prefill.enrollment || {}),
        new_course: {
          ...d.enrollment.new_course,
          ...(prefill.enrollment?.new_course || {}),
        },
      },
    }));
    if (prefill.courseMode === 'new' || prefill.enrollment?.new_course) {
      setCourseMode('new');
    }
  }, [open, prefill]);

  // Fach/Unterrichtsform aus Online-Anmeldung erst nach Stammdaten-Laden auflösen
  useEffect(() => {
    if (!open || !prefill || !subjectsRaw.length) return;
    if (!prefill.subjectHint && !prefill.teachingTypeHint) return;
    const alreadySet = data.enrollment.new_course.subject_id || data.enrollment.new_course.tariff_id;
    if (alreadySet) return;
    const resolved = resolveEnrollmentSubjectPrefill({
      subjects: subjectsRaw,
      tariffs,
      instrumentHint: prefill.subjectHint,
      teachingTypeHint: prefill.teachingTypeHint,
      durationMinutes: prefill.durationHint
        ?? (prefill.enrollment?.new_course?.duration_minutes
          ? Number(prefill.enrollment.new_course.duration_minutes)
          : null),
    });
    if (!resolved.subject_id && !resolved.tariff_id) return;
    setData((d) => ({
      ...d,
      enrollment: {
        ...d.enrollment,
        instrument: d.enrollment.instrument || prefill.subjectHint || '',
        _autoInstrument: prefill.subjectHint || d.enrollment._autoInstrument,
        new_course: {
          ...d.enrollment.new_course,
          subject_id: resolved.subject_id || d.enrollment.new_course.subject_id,
          tariff_id: resolved.tariff_id || d.enrollment.new_course.tariff_id,
        },
      },
    }));
  }, [open, prefill, subjectsRaw, tariffs]);

  // Duplikate-Check (debounced)
  useEffect(() => {
    if (!open || existingStudentId) return;
    const { first_name, last_name, date_of_birth } = data.student;
    if (!first_name.trim() || last_name.trim().length < 2) {
      setDupCandidates([]); setDupExactMatch(false); return;
    }
    if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
    dupTimerRef.current = setTimeout(() => {
      api.get('/enrollments/duplicate-check', {
        params: { first_name: first_name.trim(), last_name: last_name.trim(), date_of_birth: date_of_birth || undefined },
      }).then((r) => {
        setDupCandidates(r.data.candidates || []);
        setDupExactMatch(!!r.data.exact_match);
      }).catch(() => { setDupCandidates([]); setDupExactMatch(false); });
    }, 500);
    return () => { if (dupTimerRef.current) clearTimeout(dupTimerRef.current); };
  }, [data.student.first_name, data.student.last_name, data.student.date_of_birth, open, existingStudentId]);

  // Adress-Autovervollständigung
  const pickCourse = (course) => {
    const firstWord = (course.label || '').split(/[\s·]/)[0].trim();
    setCourseQuery(course.label);
    setCourseListOpen(false);
    setData((d) => {
      const prevAuto = d.enrollment._autoInstrument;
      const cur = d.enrollment.instrument;
      const shouldPrefill = !cur || cur === prevAuto;
      return {
        ...d,
        enrollment: {
          ...d.enrollment,
          course_id: course.value,
          instrument: shouldPrefill ? firstWord : cur,
          _autoInstrument: firstWord,
        },
      };
    });
  };

  const courseHits = (() => {
    const list = courses.filter((c) => matchesFreeText(c, courseQuery));
    return list.slice(0, 20);
  })();

  const copyPayerAddress = () => {
    const p = data.payer;
    setData((d) => ({
      ...d,
      student: {
        ...d.student,
        address: p.address || d.student.address,
        postal_code: p.postal_code || d.student.postal_code,
        city: p.city || d.student.city,
      },
    }));
    toast.success('Adresse vom Zahler übernommen');
  };

  const validate = () => {
    if (!existingStudentId && (!data.student.first_name.trim() || !data.student.last_name.trim()))
      return 'Vor- und Nachname des Schülers sind Pflicht.';
    if (!existingGuardianId && (!data.payer.first_name.trim() || !data.payer.last_name.trim()))
      return 'Vor- und Nachname des Zahlers sind Pflicht.';
    if (courseMode === 'existing' && !data.enrollment.course_id) return 'Bitte eine Veranstaltung auswählen.';
    if (courseMode === 'new' && !data.enrollment.new_course.subject_id) return 'Bitte ein Fach für den neuen Kurs auswählen.';
    if (courseMode === 'new' && !combinedMode && !data.enrollment.new_course.tariff_id) return 'Bitte einen Tarif auswählen (z. B. „Einzel 30 Min").';
    if (courseMode === 'new' && combinedMode && !data.enrollment.new_course.tariff_id) return 'Bitte einen Tarif zum gewählten Fach auswählen.';
    if (data.bank.iban && data.bank.iban.replace(/\s/g, '').length < 15) return 'IBAN scheint zu kurz.';
    if (!existingStudentId && dupExactMatch && !dupDismissed)
      return 'Möglicher Duplikat-Datensatz erkannt — bitte oben bestätigen oder abweisen.';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const enrollmentPayload = {
        start_date: isoOderLeer(data.enrollment.start_date),
        end_date: isoOderLeer(data.enrollment.end_date),
        status: data.enrollment.status || 'active',
      };
      if (courseMode === 'existing') {
        enrollmentPayload.course_id = data.enrollment.course_id;
      } else {
        const nc = data.enrollment.new_course;
        // Im Combined-Mode ist tariff_id in Wahrheit eine subject_id (die kompl. „Bass Einzel 30 Min.")
        // → Wir senden diese als subject_id, tariff_id bleibt leer.
        const realSubjectId = combinedMode ? nc.tariff_id : nc.subject_id;
        // Combined-Mode: UI speichert die Form-Subject-ID in tariff_id — echten Tarif per Name matchen
        let realTariffId = combinedMode ? null : (nc.tariff_id || null);
        let tariffName = '';
        let formName = '';
        if (combinedMode) {
          const subjRow = subjectsRaw.find((s) => s.id === realSubjectId);
          tariffName = subjRow?.name || '';
          formName = subjRow ? parseSubject(subjRow.name).form : '';
          const formNorm = (formName || '').toLowerCase().replace(/\s+/g, ' ').trim();
          const durMatch = (formName || tariffName).match(/(\d{2,3})\s*Min/i);
          const wantDur = durMatch ? durMatch[1] : null;
          const candidates = tariffs
            .map((t) => t.raw)
            .filter(Boolean)
            .map((raw) => {
              const n = (raw.name || '').toLowerCase().replace(/[.\-_/]+/g, ' ').replace(/\s+/g, ' ').trim();
              const nMin = n.replace(/(\d)\s*min(?:uten)?\.?/g, '$1min');
              const f = formNorm.replace(/(\d)\s*min(?:uten)?\.?/g, '$1min');
              if (!f || (!nMin.includes(f) && !f.includes(nMin.split(' ')[0]))) return null;
              let score = 5;
              if (nMin.includes(f) || f.includes(nMin)) score += 10;
              const tarDur = raw.duration_minutes != null ? String(raw.duration_minutes) : null;
              if (wantDur && (tarDur === wantDur || nMin.includes(`${wantDur}min`))) score += 20;
              if (/^(\d|einzel|gruppe)/i.test(raw.name || '')) score += 8;
              return {
                id: raw.id,
                score,
                amount: parseFloat(raw.amount || 0),
                name: raw.name,
                duration_minutes: raw.duration_minutes,
              };
            })
            .filter(Boolean)
            .sort((a, b) => b.score - a.score || (b.amount > 0) - (a.amount > 0));
          if (candidates[0]) {
            realTariffId = candidates[0].id;
            tariffName = candidates[0].name || tariffName;
          }
        } else {
          tariffName = tariffs.find((t) => t.value === nc.tariff_id)?.raw?.name || '';
          formName = tariffName;
        }
        const matchedTariff = tariffs.find((t) => t.value === realTariffId)?.raw;
        const durMatch = (tariffName || formName).match(/(\d{2,3})\s*Min/i);
        const durationMin = matchedTariff?.duration_minutes
          || (durMatch ? parseInt(durMatch[1], 10) : 45);
        const capMatch = (formName || tariffName || '').match(/\b(\d+)\s*-?\s*er\b/i);
        const inferredCap = capMatch ? parseInt(capMatch[1], 10) : (/gruppe/i.test(formName || tariffName || '') ? 2 : 1);
        enrollmentPayload.new_course = {
          subject_id: realSubjectId,
          teacher_id: nc.teacher_id || null,
          room_id: nc.room_id || null,
          tariff_id: realTariffId,
          form_name: formName || null,
          day_of_week: nc.day_of_week === '' ? null : parseInt(nc.day_of_week, 10),
          start_time: nc.start_time || null,
          duration_minutes: durationMin,
          rhythm: parseCourseRhythm(nc.rhythm),
          course_type: inferredCap > 1 ? 'group' : 'individual',
          max_capacity: inferredCap,
          name: (nc.name && nc.name.trim()) || autoLabel || null,
        };
      }
      const instrument = data.enrollment.needs_rental ? (data.enrollment.instrument || '').trim() : '';
      // Instrument als Prefix in Schüler-Notizen speichern (kein Schema-Change nötig)
      const studentNotes = instrument
        ? `Instrument: ${instrument}${data.student.notes ? `\n${data.student.notes}` : ''}`
        : data.student.notes;
      const payload = {
        enrollment: enrollmentPayload,
      };
      if (prefill?.profileUpdates) {
        payload.profile_updates = prefill.profileUpdates;
      }
      if (existingStudentId) {
        payload.existing_student_id = existingStudentId;
      } else {
        payload.student = { ...data.student, notes: studentNotes, date_of_birth: isoOderLeer(data.student.date_of_birth) };
      }
      if (existingGuardianId) {
        payload.existing_guardian_id = existingGuardianId;
        if ((data.bank.iban || '').trim()) {
          payload.bank = data.bank;
        }
      } else {
        payload.payer = { ...data.payer };
        payload.bank = (data.bank.iban || '').trim() ? data.bank : null;
      }
      const r = await retryOnCollision(
        () => api.post('/enrollments/quick-create', payload),
        () => api.post('/enrollments/quick-create', {
          ...payload,
          allow_schedule_collisions: true,
        }),
      );
      if (!r) return;
      const parts = [];
      if (r.data.created_new_course) parts.push('neue Veranstaltung');
      if (existingStudentId) parts.push('bestehende:r Schüler:in');
      else if (r.data.reused_guardian) parts.push(`Zahler:in „${data.payer.first_name} ${data.payer.last_name}" wiederverwendet`);
      else parts.push('Zahler:in + Schüler:in angelegt');
      toast.success(`Belegung angelegt · ${parts.join(' · ')}`);
      if (instrument) {
        toast.message(`🎵 Instrument-Miete für „${instrument}"?`, {
          description: 'Passendes Leihinstrument direkt zuweisen.',
          action: {
            label: 'Zur Miete',
            onClick: () => window.location.assign(`/instrument-rental?student_id=${r.data.student_id}&instrument_type=${encodeURIComponent(instrument)}`),
          },
          duration: 8000,
        });
      }
      onSaved && onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e, 'Anlage fehlgeschlagen'));
    } finally { setSaving(false); }
  };

  if (!open) return null;

  const payerHasAddress = !!(data.payer.address || data.payer.postal_code || data.payer.city);
  const isSibling = dupCandidates.some((c) => c.match_score < 0.95 && c.match_score >= 0.5);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6"
         data-testid={variant === 'neuerfassung' ? 'neuerfassung-dialog' : 'new-enrollment-dialog'}>
      <div className="bg-slate-50 rounded-xl shadow-2xl max-w-5xl w-full my-4">
        {/* Header */}
        <div className={`text-white px-5 py-3 rounded-t-xl flex items-center justify-between ${variant === 'neuerfassung' ? 'bg-brand-success' : 'bg-brand-anthracite'}`}>
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5" />
            <div>
              <h2 className="text-sm font-semibold tracking-wide">
                {variant === 'neuerfassung' ? 'NEUERFASSUNG' : 'NEUE BELEGUNG ANLEGEN'}
              </h2>
              <p className={`text-[11px] ${variant === 'neuerfassung' ? 'text-emerald-100' : 'text-blue-100'}`}>
                {variant === 'neuerfassung'
                  ? 'Komplette Anmeldung manuell: Schüler:in, Zahler:in, Bank und Belegung'
                  : 'Schüler:in, Zahler:in und Kurs-Belegung in einem Rutsch'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-1 rounded ${variant === 'neuerfassung' ? 'hover:bg-emerald-700' : 'hover:bg-blue-700'}`}
            data-testid="new-enrollment-dialog-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Duplikate-Warnung */}
        {dupCandidates.length > 0 && !dupDismissed && (
          <div className={`px-5 py-3 border-b flex items-start gap-3 ${dupExactMatch ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-200'}`}
               data-testid="new-enrollment-duplicate-warning">
            {dupExactMatch ? <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" /> : <Info className="w-5 h-5 text-brand-red shrink-0 mt-0.5" />}
            <div className="flex-1 text-sm">
              <div className={`font-semibold ${dupExactMatch ? 'text-rose-900' : 'text-amber-900'}`}>
                {dupExactMatch
                  ? `Möglicher Duplikat-Datensatz gefunden — bitte prüfen`
                  : (isSibling ? 'Möglicher Geschwister-Datensatz gefunden' : `${dupCandidates.length} ähnliche Person(en) gefunden`)}
              </div>
              <ul className="mt-1 space-y-0.5">
                {dupCandidates.slice(0, 3).map((c) => (
                  <li key={c.student_id} className="text-xs text-slate-700">
                    <span className="font-medium">{c.first_name} {c.last_name}</span>
                    {c.date_of_birth && <> · geb. {formatDate(c.date_of_birth)}</>}
                    {c.guardian_names.length > 0 && <> · Zahler: {c.guardian_names.join(', ')}</>}
                    {' · '}<span className="text-slate-500">{c.active_enrollments} aktive Belegung(en) · Score {Math.round(c.match_score * 100)}%</span>
                    <div className="text-[10px] text-slate-500 italic">Grund: {c.match_reasons.join(', ')}</div>
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {isSibling && (
                  <span className="text-[11px] text-amber-800 bg-amber-100 px-2 py-1 rounded">
                    💡 Bei Geschwistern greift die Mehrfächer-/Geschwister-Ermäßigung — falls konfiguriert, wird sie beim ersten Rechnungslauf automatisch angewandt.
                  </span>
                )}
                <Button size="sm" variant="outline" onClick={() => setDupDismissed(true)} data-testid="new-enrollment-dup-dismiss">
                  Andere Person — weiter
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="p-4 grid grid-cols-2 gap-4">
          {/* Column 1: Teilnehmer + Zahler */}
          <div className="space-y-3">
            <Section icon={UserIcon} title="Teilnehmer:in (Schüler:in)" color="blue"
              headerRight={payerHasAddress && !existingStudentId ? (
                <button type="button" onClick={copyPayerAddress}
                        className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded"
                        data-testid="new-enrollment-copy-payer-address">
                  <ArrowDown className="w-3 h-3" /> Adresse vom Zahler übernehmen
                </button>
              ) : existingStudentId ? (
                <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">bestehend</span>
              ) : null}
            >
              {existingStudentId ? (
                <div className="col-span-6 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2">
                  {data.student.last_name}, {data.student.first_name}
                  {data.student.date_of_birth ? ` · geb. ${data.student.date_of_birth}` : ''}
                </div>
              ) : (
                <>
              <Fld label="Vorname" value={data.student.first_name} onChange={(v) => set('student','first_name',v)} testid="ne-student-firstname" span={3} required />
              <Fld label="Nachname" value={data.student.last_name} onChange={(v) => set('student','last_name',v)} testid="ne-student-lastname" span={3} required />
              <div className="col-span-3">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Geburtsdatum</Label>
                <DateInput value={datumAusFeld(data.student.date_of_birth)} onChange={(e) => set('student','date_of_birth', datumAusFeld(e))} data-testid="ne-student-dob" className="h-8 text-sm" />
              </div>
              <Fld label="E-Mail" value={data.student.email} onChange={(v) => set('student','email',v)} testid="ne-student-email" span={3} type="email" />
              <Fld label="Telefon" value={data.student.phone} onChange={(v) => set('student','phone',v)} testid="ne-student-phone" span={3} />
              <Fld label="Adresse" value={data.student.address} onChange={(v) => set('student','address',v)} testid="ne-student-address" span={6} placeholder="wird sonst vom Zahler übernommen" />
              <Fld label="PLZ" value={data.student.postal_code} onChange={(v) => set('student','postal_code',v)} testid="ne-student-plz" span={2} />
              <Fld label="Ort" value={data.student.city} onChange={(v) => set('student','city',v)} testid="ne-student-city" span={4} />
              <div className="col-span-6">
                <PlzOrtBridge
                  postalCode={data.student.postal_code}
                  city={data.student.city}
                  onCity={(value) => set('student', 'city', value)}
                  testId="ne-student-plz-ort"
                />
              </div>
              <SelFld label="Areal" value={data.student.areal} onValueChange={(v) => set('student','areal',v)} options={AREAL_OPTIONS} span={6} testid="ne-student-areal" />
              <Fld label="Notizen" value={data.student.notes} onChange={(v) => set('student','notes',v)} testid="ne-student-notes" span={6} />
                </>
              )}
            </Section>

            <Section icon={Users} title="Zahlungspflichtige:r" color="amber"
              headerRight={existingGuardianId ? (
                <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">bestehend</span>
              ) : null}
            >
              {existingGuardianId ? (
                <div className="col-span-6 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2">
                  {data.payer.last_name}, {data.payer.first_name}
                  {data.payer.email ? ` · ${data.payer.email}` : ''}
                </div>
              ) : (
                <>
              <Fld label="Vorname" value={data.payer.first_name} onChange={(v) => set('payer','first_name',v)} testid="ne-payer-firstname" span={3} required />
              <Fld label="Nachname" value={data.payer.last_name} onChange={(v) => set('payer','last_name',v)} testid="ne-payer-lastname" span={3} required />
              <Fld label="E-Mail" value={data.payer.email} onChange={(v) => set('payer','email',v)} testid="ne-payer-email" span={3} type="email" placeholder="dient auch als Wiederverwendungs-Key" />
              <Fld label="Telefon" value={data.payer.phone} onChange={(v) => set('payer','phone',v)} testid="ne-payer-phone" span={3} />
              <Fld label="Adresse" value={data.payer.address} onChange={(v) => set('payer','address',v)} testid="ne-payer-address" span={6} />
              <Fld label="PLZ" value={data.payer.postal_code} onChange={(v) => set('payer','postal_code',v)} testid="ne-payer-plz" span={2} />
              <Fld label="Ort" value={data.payer.city} onChange={(v) => set('payer','city',v)} testid="ne-payer-city" span={4} />
              <div className="col-span-6">
                <PlzOrtBridge
                  postalCode={data.payer.postal_code}
                  city={data.payer.city}
                  onCity={(value) => set('payer', 'city', value)}
                  testId="ne-payer-plz-ort"
                />
              </div>
                </>
              )}
            </Section>
          </div>

          {/* Column 2: Belegungsdetails + Bank */}
          <div className="space-y-3">
            <Section icon={GraduationCap} title="Belegungsdetails" color="emerald"
              headerRight={
                <div className="flex items-center gap-1 bg-slate-100 rounded p-0.5">
                  <button type="button"
                          onClick={() => setCourseMode('existing')}
                          className={`text-[10px] px-2 py-1 rounded ${courseMode === 'existing' ? 'bg-white shadow font-semibold text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
                          data-testid="ne-mode-existing">
                    Veranstaltung suchen
                  </button>
                  <button type="button"
                          onClick={() => setCourseMode('new')}
                          className={`text-[10px] px-2 py-1 rounded ${courseMode === 'new' ? 'bg-white shadow font-semibold text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
                          data-testid="ne-mode-new">
                    Neue Veranstaltung
                  </button>
                </div>
              }
            >
              {courseMode === 'existing' ? (
                <>
                  <div className="col-span-6 relative">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      Veranstaltung <span className="text-rose-500 ml-0.5">*</span>
                    </Label>
                    <Input
                      value={courseQuery}
                      onChange={(e) => {
                        setCourseQuery(e.target.value);
                        setCourseListOpen(true);
                        if (data.enrollment.course_id) set('enrollment', 'course_id', '');
                      }}
                      onFocus={() => setCourseListOpen(true)}
                      onBlur={() => setTimeout(() => setCourseListOpen(false), 150)}
                      placeholder={loadingCourses ? 'Veranstaltungen werden geladen…' : 'Veranstaltung suchen'}
                      className="h-8 text-sm"
                      data-testid="ne-enrollment-course"
                      role="combobox"
                      aria-expanded={courseListOpen}
                    />
                    {courseListOpen && (
                      <ul
                        className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg"
                        data-testid="ne-course-hits"
                      >
                        {courseHits.length === 0 ? (
                          <li className="px-2 py-1.5 text-xs text-slate-400">Keine Veranstaltung gefunden</li>
                        ) : courseHits.map((c) => (
                          <li key={c.value}>
                            <button
                              type="button"
                              role="option"
                              className="w-full text-left px-2 py-1.5 text-sm hover:bg-slate-50"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => pickCourse(c)}
                            >
                              {c.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="col-span-6 pt-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={data.enrollment.needs_rental}
                        onChange={(e) => set('enrollment', 'needs_rental', e.target.checked)}
                        className="w-4 h-4 accent-blue-600"
                        data-testid="ne-needs-rental"
                      />
                      <span className="text-sm text-slate-700">
                        <span className="font-medium">Leihinstrument benötigt?</span>
                        <span className="text-slate-500 ml-1">
                          — z. B. wenn der Schüler noch kein eigenes Instrument besitzt
                        </span>
                      </span>
                    </label>
                  </div>
                  {data.enrollment.needs_rental && (
                    <div className="col-span-6">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Instrument</Label>
                      <Input
                        value={data.enrollment.instrument}
                        onChange={(e) => set('enrollment','instrument',e.target.value)}
                        list="ne-instrument-list"
                        placeholder="z. B. Klavier, Gitarre, Violine"
                        className="h-8 text-sm"
                        data-testid="ne-instrument"
                      />
                      <p className="text-[10px] text-slate-400 italic mt-1">Nach Anlage der Belegung wird die Vermietung als Aktion angeboten.</p>
                      <datalist id="ne-instrument-list">
                        {instrumentTypes.map((t) => <option key={t} value={t} />)}
                      </datalist>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <SelFld label="Fach" value={data.enrollment.new_course.subject_id}
                          onValueChange={(v) => {
                            setNc('subject_id', v);
                            if (combinedMode) setNc('tariff_id', '');
                            // Instrument aus Fach ableiten (nur vorbelegen, User-Overrides werden respektiert)
                            let instrName = '';
                            if (combinedMode && v?.startsWith('__combined__')) {
                              instrName = v.replace('__combined__', '');
                            } else {
                              instrName = subjectsRaw.find((s) => s.id === v)?.name || '';
                            }
                            // Nur vorbelegen wenn Feld leer ODER Wert stammt von vorheriger Fach-Auswahl
                            setData((d) => {
                              const prevAuto = d.enrollment._autoInstrument;
                              const currentInstr = d.enrollment.instrument;
                              const shouldPrefill = !currentInstr || currentInstr === prevAuto;
                              return {
                                ...d,
                                enrollment: {
                                  ...d.enrollment,
                                  instrument: shouldPrefill ? instrName : currentInstr,
                                  _autoInstrument: instrName,
                                },
                              };
                            });
                          }}
                          options={subjects} span={3} required testid="ne-newcourse-subject"
                          placeholder="Instrument wählen" />
                  <SelFld label="Tarif" value={data.enrollment.new_course.tariff_id}
                          onValueChange={(v) => setNc('tariff_id', v)}
                          options={
                            combinedMode
                              ? (() => {
                                  const instr = data.enrollment.new_course.subject_id?.replace('__combined__','');
                                  return instr && subjectFormsByInstrument[instr] ? subjectFormsByInstrument[instr] : [];
                                })()
                              : tariffs
                          }
                          span={3} required testid="ne-newcourse-tariff"
                          placeholder={combinedMode && !data.enrollment.new_course.subject_id ? 'Zuerst Fach wählen' : 'z. B. Einzel 30 Min'} />
                  <SelFld label="Lehrkraft" value={data.enrollment.new_course.teacher_id}
                          onValueChange={(v) => setNc('teacher_id', v)}
                          options={teachers} span={3} testid="ne-newcourse-teacher" />
                  <SelFld label="Raum" value={data.enrollment.new_course.room_id}
                          onValueChange={(v) => setNc('room_id', v)}
                          options={rooms} span={3} testid="ne-newcourse-room" />
                  <SelFld label="Wochentag" value={data.enrollment.new_course.day_of_week}
                          onValueChange={(v) => setNc('day_of_week', v)}
                          options={DAYS} span={2} testid="ne-newcourse-day" />
                  <div className="col-span-2">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Uhrzeit</Label>
                    <Input type="time" value={formatTime(data.enrollment.new_course.start_time) || ''}
                           onChange={(e) => setNc('start_time', e.target.value)}
                           className="h-8 text-sm" data-testid="ne-newcourse-time" />
                  </div>
                  <SelFld label="Turnus" value={data.enrollment.new_course.rhythm || 'weekly'}
                          onValueChange={(v) => setNc('rhythm', v)}
                          options={TURNUS_OPTIONS}
                          span={2} testid="ne-newcourse-rhythm" />
                  <div className="col-span-6">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      Bezeichnung (optional, sonst automatisch)
                    </Label>
                    <Input
                      value={data.enrollment.new_course.name}
                      onChange={(e) => setNc('name', e.target.value)}
                      placeholder={autoLabel || 'wird aus Fach · Tarif · Zeit generiert'}
                      className="h-8 text-sm"
                      data-testid="ne-newcourse-name"
                    />
                    {autoLabel && !data.enrollment.new_course.name && (
                      <p className="text-[10px] text-slate-400 italic mt-1" data-testid="ne-newcourse-name-preview">
                        Vorschau: <span className="text-slate-500 font-medium">{autoLabel}</span>
                      </p>
                    )}
                  </div>
                  <div className="col-span-6 pt-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={data.enrollment.needs_rental}
                        onChange={(e) => set('enrollment', 'needs_rental', e.target.checked)}
                        className="w-4 h-4 accent-blue-600"
                        data-testid="ne-needs-rental-newcourse"
                      />
                      <span className="text-sm text-slate-700">
                        <span className="font-medium">Leihinstrument benötigt?</span>
                        <span className="text-slate-500 ml-1">— z. B. bei Anfänger:innen ohne eigenes Instrument</span>
                      </span>
                    </label>
                  </div>
                  {data.enrollment.needs_rental && (
                    <div className="col-span-6">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Instrument</Label>
                      <Input
                        value={data.enrollment.instrument}
                        onChange={(e) => set('enrollment','instrument',e.target.value)}
                        list="ne-instrument-list-nc"
                        placeholder="wird aus Fach vorbelegt — kann überschrieben werden"
                        className="h-8 text-sm"
                        data-testid="ne-instrument-newcourse"
                      />
                      <datalist id="ne-instrument-list-nc">
                        {instrumentTypes.map((t) => <option key={t} value={t} />)}
                      </datalist>
                    </div>
                  )}
                </>
              )}
              <div className="col-span-3">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Eintritt</Label>
                <DateInput value={datumAusFeld(data.enrollment.start_date)} onChange={(e) => set('enrollment','start_date', datumAusFeld(e))} data-testid="ne-enrollment-start" className="h-8 text-sm" />
              </div>
              <div className="col-span-3">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Austritt (optional)</Label>
                <DateInput value={datumAusFeld(data.enrollment.end_date)} onChange={(e) => set('enrollment','end_date', datumAusFeld(e))} data-testid="ne-enrollment-end" className="h-8 text-sm" />
              </div>
            </Section>

            <Section icon={Euro} title="Bankdaten (SEPA-Mandat, optional)" color="brand">
              <Fld label="Kontoinhaber:in" value={data.bank.account_holder} onChange={(v) => set('bank','account_holder',v)} testid="ne-bank-holder" span={6} placeholder="falls leer: Zahler:in-Name" />
              <Fld
                label="IBAN"
                value={data.bank.iban}
                onChange={(v) => {
                  set('bank', 'iban', v);
                  const cleaned = String(v || '').replace(/[\s-]/g, '');
                  if (cleaned.length < 15) return;
                  lookupIbanInfo(cleaned).then((info) => {
                    if (!info?.bic) return;
                    setData((d) => ({
                      ...d,
                      bank: { ...d.bank, iban: v, bic: info.bic },
                    }));
                  });
                }}
                testid="ne-bank-iban"
                span={4}
                placeholder="DE… — leer lassen wenn kein Lastschrifteinzug"
              />
              <Fld label="BIC" value={data.bank.bic} onChange={(v) => set('bank','bic',v)} testid="ne-bank-bic" span={2} placeholder="aus IBAN" />
            </Section>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white border-t border-slate-200 px-5 py-3 rounded-b-xl flex items-center justify-between">
          <p className="text-xs text-slate-500">
            <span className="text-rose-500">*</span> Pflichtfelder. Zahler-E-Mail = Wiederverwendungs-Key für Geschwister.
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving} data-testid="new-enrollment-cancel">
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-brand-anthracite hover:bg-brand-anthracite/90" data-testid="new-enrollment-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Speichern
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
